import type { CSSProperties, ReactNode } from 'react';
import React from 'react';

import type { ReferenceType } from '@floating-ui/react-dom';
import { autoUpdate, flip, inline, offset, shift, useFloating } from '@floating-ui/react-dom';
import { themeV5 } from 'src/theme';

/**
 * Default styles that should be applied to MUI Paper components used inside editor popovers.
 */
export const defaultEditorPopoverPaperStyles: CSSProperties = {
  boxShadow: 'none',
  boxSizing: 'border-box',
  cursor: 'default',
  filter: `drop-shadow(${themeV5.appShadows.popper})`,
  minWidth: 312,
};

export interface EditorPopoverProps {
  children: ReactNode;
  placement: 'top' | 'bottom';
  reference: ReferenceType | null;
  gap?: number;
}

export function EditorPopover({ children, gap = 4, placement, reference }: EditorPopoverProps) {
  const { floatingStyles, refs } = useFloating({
    elements: { reference },
    middleware: [inline(), flip(), offset(gap), shift({ padding: gap })],
    placement,
    whileElementsMounted: autoUpdate,
  });

  return (
    <div
      ref={refs.setFloating}
      style={{ ...floatingStyles, pointerEvents: 'auto', zIndex: themeV5.zIndex.modal - 1 }}
      /** Do not let mouse events inside the popover affect the page */
      onClick={stopPropagation}
      onContextMenu={stopPropagation}
      onMouseDown={stopPropagation}
      onMouseMove={stopPropagation}
      onMouseUp={stopPropagation}
    >
      {children}
    </div>
  );
}

function stopPropagation(event: React.MouseEvent): void {
  event.stopPropagation();
}
